import uiUtils from './uiUtils'
import deepmerge from 'deepmerge'
import { useI18n } from 'vue-i18n'
import { useConfiguration } from 'ui-common/base/composables/useConfiguration.pinia'

const commonRcProps = {
  energyType: {
    type: String,
    required: true
  },
  title: {
    type: String,
    default: () => null
  },
  zipCode: {
    type: String,
    default: () => ''
  },
  city: {
    type: String,
    default: () => null
  },
  street: {
    type: String,
    default: () => null
  },
  houseNumber: {
    type: String,
    default: () => null
  },
  mpid: {
    type: String,
    default: () => null
  },
  consumption: {
    type: [Number, null],
    default: () => null
  },
  disableAddressInput: {
    type: Boolean,
    default: () => false
  },
  textColor: {
    type: String,
    default: () => 'white'
  },
  inputTextColor: {
    type: String,
    default: () => 'dark'
  },
  inputBgColor: {
    type: String,
    default: () => 'white'
  },
  titleIcon: {
    type: String,
    default: () => null
  },
  filterProps: {
    type: Array,
    default: null
  },
  onlyCleanPower: {
    type: Boolean,
    default: () => false
  },
  targetGroup: {
    type: String,
    default: () => 'PRIVATE_CUSTOMER',
    validator: v => ['PRIVATE_CUSTOMER', 'BUSINESS_CUSTOMER'].includes(v)
  },
  searching: {
    type: Boolean,
    default: () => false
  },
  consumptionLabel: {
    type: String,
    default: () => 'Verbrauch'
  },
  zipCodeLabel: {
    type: String,
    default: () => 'PLZ'
  }
}

export const rcProps = {
  ...commonRcProps,
  unit: {
    type: String,
    default: () => null
  },
  showEquivalentInput: {
    type: Boolean,
    default: () => true
  },
  consumptionPerEquivalent: {
    type: Number,
    default: () => null
  },
  minEquivalent: {
    type: Number,
    default: () => null
  },
  maxEquivalent: {
    type: Number,
    default: () => null
  },
  equivalentLabel: {
    type: String,
    default: () => null
  },
  equivalentUnit: {
    type: String,
    default: () => ''
  },
  equivalentIconLow: {
    type: String,
    default: () => null
  },
  equivalentIconHigh: {
    type: String,
    default: () => null
  }
}

export const rcPropsElectricHeating = {
  ...commonRcProps,
  consumptionNT: {
    type: [Number, null],
    default: () => null
  },
  disableCategoryInput: {
    type: Boolean,
    default: () => false
  },
  disableMeterTypeInput: {
    type: Boolean,
    default: () => false
  },
  category: {
    type: String,
    default: () => 'HEAT_PUMP'
  },
  multipleCounters: {
    type: Boolean,
    default: () => false
  },
  separateMeterForHeating: {
    type: Boolean,
    default: () => false
  }
}

export const selectionProps = {
  queryDataProvider: {
    type: Object,
    requred: true
  },
  fromContract: {
    type: Boolean,
    default: () => false
  },
  paramsResolved: {
    type: Boolean,
    default: () => false
  },
  energyType: {
    type: String,
    default: () => null
  },
  tag: {
    type: Array,
    default: () => null
  },
  productId: {
    type: String,
    default: () => null
  }
}

export const useSearchConfiguration = () => {
  const i18n = useI18n()
  const { brandConfig, getContextConfig } = useConfiguration()

  const getSearchConfiguration = (override) => {
    const defaultLocationValues = {
      zipCode: '',
      city: null,
      street: null,
      houseNumber: null,
      mpid: null
    }
    const defaultConfig = {
      electricity: {
        rcProps: {
          ...defaultLocationValues,
          showEquivalentInput: true,
          titleIcon: uiUtils.typeIcon('ELECTRICITY'),
          title: i18n.t('energyTypes.electricity'),
          unit: 'kWh',
          minEquivalent: 1,
          maxEquivalent: 9,
          consumption: 2400,
          consumptionPerEquivalent: 1200,
          onlyCleanPower: false,
          targetGroup: 'PRIVATE_CUSTOMER',
          equivalentIconLow: 'person',
          equivalentIconHigh: 'people',
          equivalentLabel: i18n.t('equivalent.electricity.peopleInHome'),
          filterProps: [
            {
              type: 'checkbox',
              name: 'onlyCleanPower',
              // or is deliberate to disregard empty strings
              label: getContextConfig('orders.electricity.onlyCleanPowerText') || i18n.t('filterProperties.electricity.onlyCleanPower'),
              class: 'col-md-4 col-sm-6 col-xs-12',
              disabled: !getContextConfig('orders.electricity.onlyCleanPowerActive', false)
            },
            {
              type: 'toggle',
              name: 'targetGroup',
              offLabel: i18n.t('filterProperties.electricity.targetGroup.private'),
              onLabel: i18n.t('filterProperties.electricity.targetGroup.business'),
              offValue: 'PRIVATE_CUSTOMER',
              onValue: 'BUSINESS_CUSTOMER',
              class: 'col-md-4 col-sm-6 col-xs-12',
              disabled: !getContextConfig('orders.businessCustomersActive', false)
            }
          ].filter(c => !c.disabled)
        },
        sortOrder: ['CATEGORY_ASC', 'SUM_ASC']
      },
      natural_gas: {
        rcProps: {
          ...defaultLocationValues,
          showEquivalentInput: true,
          titleIcon: uiUtils.typeIcon('NATURAL_GAS'),
          title: i18n.t('energyTypes.natural_gas'),
          unit: 'kWh',
          consumption: 9600,
          consumptionPerEquivalent: 160,
          onlyCleanPower: false,
          targetGroup: 'PRIVATE_CUSTOMER',
          equivalentIconLow: 'home',
          equivalentIconHigh: 'business',
          equivalentLabel: i18n.t('equivalent.natural_gas.livingSpace'),
          equivalentUnit: 'm²',
          minEquivalent: 30,
          maxEquivalent: 240,
          filterProps: [
            {
              type: 'checkbox',
              name: 'onlyCleanPower',
              label: getContextConfig('orders.natural_gas.onlyEcoGasText') || i18n.t('filterProperties.natural_gas.onlyEcoGas'),
              class: 'col-md-4 col-sm-6 col-xs-12',
              disabled: !getContextConfig('orders.natural_gas.onlyEcoGasActive')
            },
            {
              type: 'toggle',
              name: 'targetGroup',
              offLabel: i18n.t('filterProperties.electricity.targetGroup.private'),
              onLabel: i18n.t('filterProperties.electricity.targetGroup.business'),
              offValue: 'PRIVATE_CUSTOMER',
              onValue: 'BUSINESS_CUSTOMER',
              class: 'col-md-4 col-sm-6 col-xs-12',
              disabled: !getContextConfig('orders.businessCustomersActive', false)
            }
          ].filter(c => !c.disabled)
        }
      },
      electric_heating: {
        rcProps: {
          ...defaultLocationValues,
          titleIcon: uiUtils.typeIcon('ELECTRIC_HEATING'),
          title: i18n.t('energyTypes.electric_heating'),
          // unit: 'kWh',
          category: 'HEAT_PUMP',
          targetGroup: 'PRIVATE_CUSTOMER',
          separateMeterForHeating: true,
          multipleCounters: false,
          consumption: null,
          consumptionNT: null,
          counterTypes: null
        }
      }
    }

    let merged = defaultConfig

    if (override) {
      merged = deepmerge(defaultConfig, override, {
        arrayMerge: (target, source) => {
          return source
        }
      })
      Object.entries(merged).forEach(([energyTypeKey, configuration]) => {
        const overrideEtFilterConfig = override[energyTypeKey]?.filterConfig
        if (overrideEtFilterConfig && configuration.rcProps.filterProps) {
          configuration.rcProps.filterProps.forEach((f, index) => {
            const filterOverride = overrideEtFilterConfig[f.name]
            if (filterOverride) {
              configuration.rcProps.filterProps[index] = deepmerge(f, filterOverride)
            }
          })
        }
      })
    }
    return merged
  }

  const getAvailableConfig = () => {
    const configuredEnergyTypes = getContextConfig('orders.availableEnergyTypes').map(s => s.toUpperCase())
    const searchConfigOverride = brandConfig.value.get('searchConfigOverride', null)

    const searchConfiguration = getSearchConfiguration(searchConfigOverride)

    if (configuredEnergyTypes) {
      const entries = Object.entries(searchConfiguration).filter(([k]) => configuredEnergyTypes.includes(k.toUpperCase()))
      return Object.fromEntries(entries)
    }

    return searchConfiguration
  }

  return { getAvailableConfig }
}
